// Reactive event dispatcher. ADR 0014 §2.1 (機構 2) / §2.4 / §2.9 + ADR 0015 §4.7。
//
// 18 ActivationEvent (既存 4 + 新規 14) の各発火点から呼ばれる helper。
// caster の `itemSkills` を走査し、`activation.onEvent === event` 一致の Skill
// を Activation 評価 → cooldown チェック → proc chance roll → applyEffects のフルパスで発火する。
//
// 発火粒度規約 (ADR 0014 §2.4):
//   - 1 tick 1 skill 1 回集約 (`firedReactiveThisTick` で重複検知)
//   - caster (owner) 自身の itemSkills のみ (味方/敵の Skill は発火しない)
//   - 連鎖深度 1 hop まで (同 tick 内の再発火を阻止 = 連鎖暴走防止)
//
// proc chance ロール (ADR 0015 §4.7 / §7.2):
//   - Activation 通過後に `readBaseProcChance(itemSkill) + jobTypeBonus + affix(=0)`
//     を `resolveProcChance` で 80% クランプ → `rollProc(state.rng)` で発火可否
//   - jobTypeBonus は `evaluateActivation` の戻り値 `fireChanceMod` を流用
//     (Activation 内部で `caster.jobTypes.has(jobType)` 判定済)
//
// CD axis6 layer (ADR 0015 §4.3 / §7.3):
//   - `getAxis6CdrLayer(caster)` で 軸 6 archetype 4 cooldown-reduction 装備の合計を取得
//   - `applyCooldownRateMod` の `layers.axis6` に渡し 30% でクランプ
import { evaluateActivation } from '../activation.js';
import { getSkill } from '../content/skills.js';
import { applyEffects } from '../effects.js';
import { applyCooldownRateMod } from './cd-clamp.js';
import { getAxis6CdrLayer } from './cd-layers.js';
import { triggerManualPlaceables } from './placeables.js';
import { readBaseProcChance, resolveProcChance, rollProc } from './proc-chance.js';
/** Default cooldown applied when a reactive Skill omits `cooldownTicks`. 軸 8 archetype 7 §6.3 の iCD = 30 ticks を最小値として採用。 */
const DEFAULT_REACTIVE_COOLDOWN_TICKS = 30;
/**
 * Fire all reactive / trigger skills on `casterId` whose `activation.onEvent`
 * matches `event` and pass `evaluateActivation` + cooldown checks.
 *
 * @param state battle state (mutated: cooldowns, events, firedReactiveThisTick)
 * @param casterId entity id whose itemSkills are queried
 * @param event matched ActivationEvent literal
 * @param primaryTarget primary target for `applyEffects` (event-specific; e.g.
 *   attacker for onTakenDamage, victim for onKill, target for onHit). May be
 *   null for self-targeted events (onHpBelow / onSelfDebuffed). The reactive
 *   Skill's own `targeting` rule then re-selects per its shape.
 */
export function fireReactiveSkills(state, casterId, event, primaryTarget) {
    // ADR 0014 §2.4 連鎖深度 1 hop ガード: reactive 発火中の再入は無条件 skip。
    // (1 hop = 元イベント → reactive Skill 発火 → その効果が新たな reactive を
    // 連鎖発火させない。深度 0 でのみ受け付ける。)
    if (state.reactiveChainDepth > 0)
        return;
    const caster = state.entities.find((e) => e.id === casterId);
    if (caster === undefined || caster.hp <= 0)
        return;
    const fired = ensureFiredSet(state, casterId);
    const tick = state.tick;
    state.reactiveChainDepth += 1;
    try {
        for (const itemSkill of caster.itemSkills) {
            const skill = getSkill(itemSkill.templateId);
            if (skill === undefined)
                continue;
            if (skill.activation.onEvent !== event)
                continue;
            if (skill.kind !== 'trigger' && skill.kind !== 'reactive')
                continue;
            // ADR 0014 §2.4: 1 tick 1 skill 1 回集約。
            if (fired.has(skill.id))
                continue;
            // CD ready チェック
            const cdReady = (caster.cooldowns.get(skill.id) ?? 0) <= tick;
            if (!cdReady)
                continue;
            // Activation 評価 (require* 系)
            const act = evaluateActivation(skill, caster, primaryTarget, state);
            if (!act.canFire)
                continue;
            // ADR 0015 §4.7 / §7.2: proc chance ロール (Activation 通過後)。
            // base = ItemSkill.values[tier] (未設定なら values[0] fallback)
            // jobTypeBonus = act.fireChanceMod (Activation 内 jobTypeBonus 一致時のみ非 0)
            // affix = 0 (将来 4.5 affix で投入)
            // → resolveProcChance で 80% クランプ → rollProc で抽選
            const chance = resolveProcChance({
                base: readBaseProcChance(itemSkill),
                jobTypeBonus: act.fireChanceMod,
                affix: 0,
            });
            // proc-chance.ts の `rollProc` は `() => number` を期待するため、
            // BattleState.rng (xoshiro128++ Rng クラス) を nextFloat() ラムダで包む。
            if (!rollProc(chance, () => state.rng.nextFloat()))
                continue;
            // proc 通過: 発火確定。1 tick 1 skill 1 回集約。
            fired.add(skill.id);
            // ADR 0014 §2.5 / ADR 0015 §4.3: cooldown は CD 30% クランプ経由で設定。
            // axis6 layer は cd-layers.ts の getAxis6CdrLayer から取得。
            const baseCd = skill.cooldownTicks ?? DEFAULT_REACTIVE_COOLDOWN_TICKS;
            const effectiveCd = applyCooldownRateMod(baseCd, {
                affix: 0,
                axis6: getAxis6CdrLayer(caster),
                other: 0,
            });
            caster.cooldowns.set(skill.id, tick + effectiveCd);
            state.events.push({
                kind: 'skillCast',
                tick,
                sourceId: caster.id,
                skillId: skill.id,
                ...(primaryTarget !== null ? { targetId: primaryTarget.id } : {}),
            });
            // ADR 0015 §4.8: effectAmountMod は damage/heal/shield の power のみ乗算。
            applyEffects(skill, caster, primaryTarget, state, {
                effectAmountMod: act.effectAmountMod,
                sourceItemSkill: itemSkill,
            });
            triggerManualPlaceables(state, caster, skill.id);
        }
    }
    finally {
        state.reactiveChainDepth -= 1;
    }
}
function ensureFiredSet(state, casterId) {
    let s = state.firedReactiveThisTick.get(casterId);
    if (s === undefined) {
        s = new Set();
        state.firedReactiveThisTick.set(casterId, s);
    }
    return s;
}
//# sourceMappingURL=reactive-events.js.map